import { useMemo, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ExternalLink, Loader2, PlusCircle, SkipForward } from 'lucide-react';
import type { ImportConflictRow } from '@/hooks/useCSVImport';
import { plainLanguageConflictExplanation } from '@/lib/csvImportConflicts';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { log } from '@/lib/logger';

interface CSVCompanyConflictReviewProps {
  jobId: string | null;
  workspaceId: string | null;
  conflicts: ImportConflictRow[];
  parsedData: Record<string, string>[];
  /** Set inside a dialog that already scrolls, so the list adds no second scrollbar. */
  uncapped?: boolean;
  onOpenCompany: (companyId: string) => void;
  onResolved: (row: number, action: 'forced' | 'skipped') => void;
}

function companyPayloadFromConflict(
  conflict: ImportConflictRow,
  data: Record<string, string>,
): Record<string, string | undefined> {
  const companyName =
    (conflict.companyName || conflict.name || '').trim() || `Row ${conflict.row}`;
  const website =
    data.website ||
    data.Website ||
    data['company.website'] ||
    (conflict.domain ? `https://${conflict.domain}` : undefined);
  return {
    name: companyName,
    domain: conflict.domain || data.domain || data.Domain || undefined,
    industry: data.industry || data['company.industry'] || undefined,
    description: data.description || undefined,
    website: website || undefined,
  };
}

export function CSVCompanyConflictReview({
  jobId,
  workspaceId,
  conflicts,
  parsedData,
  onOpenCompany,
  uncapped = false,
  onResolved,
}: CSVCompanyConflictReviewProps) {
  const [busyRow, setBusyRow] = useState<number | null>(null);
  const [localDone, setLocalDone] = useState<Set<number>>(() => new Set());
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});

  const remaining = useMemo(
    () => conflicts.filter((c) => !localDone.has(c.row)),
    [conflicts, localDone],
  );

  if (conflicts.length === 0) return null;

  async function forceCreate(conflict: ImportConflictRow) {
    if (!workspaceId) return;
    setBusyRow(conflict.row);
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[conflict.row];
      return next;
    });
    try {
      const data = parsedData[conflict.row - 1] ?? {};
      const company = companyPayloadFromConflict(conflict, data);
      const result = await invokeEdgeFunction<{
        success?: boolean;
        company_id?: string;
        message?: string;
      }>('resolve-csv-company-conflict', {
        body: {
          action: 'force_create',
          workspaceId,
          jobId,
          row: conflict.row,
          company,
        },
        timeoutMs: 60_000,
      });
      if (!result?.success) {
        throw new Error(result?.message || 'Could not create company');
      }
      setLocalDone((prev) => new Set(prev).add(conflict.row));
      onResolved(conflict.row, 'forced');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Force create failed';
      log.warn('CSV conflict force create failed', { row: conflict.row, message });
      setRowErrors((prev) => ({ ...prev, [conflict.row]: message }));
    } finally {
      setBusyRow(null);
    }
  }

  function skip(conflict: ImportConflictRow) {
    setLocalDone((prev) => new Set(prev).add(conflict.row));
    onResolved(conflict.row, 'skipped');
  }

  function skipAllRemaining() {
    for (const c of remaining) {
      onResolved(c.row, 'skipped');
    }
    setLocalDone((prev) => {
      const next = new Set(prev);
      for (const c of remaining) next.add(c.row);
      return next;
    });
  }

  return (
    <Card className="p-4 space-y-3 border-warning/40 bg-warning/5">
      <div className="space-y-2">
        <h4 className="font-semibold text-sm">
          {remaining.length > 0
            ? `${remaining.length} compan${remaining.length === 1 ? 'y' : 'ies'} need a quick choice`
            : 'All set — every conflict has a choice'}
        </h4>
        <p className="text-xs text-muted-foreground">
          These are not crashes. Nynch already has a company with the same name
          (usually a different website), so we paused instead of creating duplicates.
          Your import data is safe.
        </p>
        {remaining.length > 1 ? (
          <div className="rounded-md border border-border/60 bg-background/90 p-3 space-y-2">
            <p className="text-xs text-muted-foreground">
              For a large list, the usual choice is to keep what is already in Nynch
              and move on. You can always fix a single company later from Companies.
            </p>
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs"
              disabled={busyRow !== null}
              onClick={skipAllRemaining}
            >
              <SkipForward className="h-3 w-3 mr-1" />
              Skip all remaining (recommended)
            </Button>
          </div>
        ) : null}
      </div>

      <ul className={uncapped ? "space-y-3" : "space-y-3 max-h-72 overflow-y-auto"}>
        {remaining.slice(0, 50).map((c) => {
          const companyLabel = (c.companyName || c.name || `Row ${c.row}`).trim();
          const explanation = plainLanguageConflictExplanation(c.error);
          return (
            <li
              key={c.row}
              className="rounded-md border border-border/60 bg-background/80 p-3 space-y-2"
            >
              <div className="text-sm space-y-0.5">
                <div>
                  <span className="text-muted-foreground">Company:</span>{' '}
                  <span className="font-medium">{companyLabel}</span>
                  {c.domain ? (
                    <span className="text-muted-foreground"> · {c.domain}</span>
                  ) : null}
                </div>
                {c.personName ? (
                  <div className="text-xs text-muted-foreground">
                    Person in spreadsheet: {c.personName}
                    <span className="text-muted-foreground/80"> · CSV row {c.row}</span>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">CSV row {c.row}</div>
                )}
                {c.existingCompanyName ? (
                  <div className="text-xs text-muted-foreground">
                    Already in Nynch:{' '}
                    <span className="font-medium text-foreground">{c.existingCompanyName}</span>
                    {c.existingCompanyWebsite
                      ? ` · ${c.existingCompanyWebsite}`
                      : ''}
                  </div>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">{explanation}</p>
              {rowErrors[c.row] ? (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription className="text-xs">{rowErrors[c.row]}</AlertDescription>
                </Alert>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {c.existingCompanyId ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => onOpenCompany(c.existingCompanyId!)}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    See company in Nynch
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="h-8 text-xs"
                  disabled={busyRow === c.row}
                  onClick={() => skip(c)}
                >
                  <SkipForward className="h-3 w-3 mr-1" />
                  Keep what Nynch has
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 text-xs"
                  disabled={busyRow === c.row}
                  onClick={() => forceCreate(c)}
                  title="Only if these are truly different businesses with the same name"
                >
                  {busyRow === c.row ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <PlusCircle className="h-3 w-3 mr-1" />
                  )}
                  Create a second company
                </Button>
              </div>
              <p className="text-caption text-muted-foreground">
                <span className="font-medium text-foreground/80">Keep what Nynch has</span>
                {' '}— most common for bulk imports.{' '}
                <span className="font-medium text-foreground/80">Create a second company</span>
                {' '}— only when they are really different businesses.
              </p>
            </li>
          );
        })}
        {remaining.length > 50 ? (
          <li className="text-xs text-muted-foreground">
            …and {remaining.length - 50} more. Use “Skip all remaining” or download the
            import report for the full list.
          </li>
        ) : null}
      </ul>
    </Card>
  );
}
